import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { useLibraryDeps } from '../LibraryDeps'
import { BadgeControl, type ItemBadges } from './BadgeControl'
import { NotesEditor } from './NotesEditor'
import type { VaultItem } from './vaultData'

interface Props {
  items: VaultItem[]
  index: number
  onIndex: (i: number) => void
  onClose: () => void
  /** More items exist past the loaded ones: → at the end loads them. */
  hasMore: boolean
  onLoadMore: () => Promise<void>
  selected?: boolean
  onToggle?: () => void
  /** The phone layout: full-bleed media, controls top and bottom. */
  mobile?: boolean
  /** The shown item's description; absent when notes are unavailable. */
  notes?: { value: string; onSave?: (text: string) => Promise<void> }
  /** The shown item's Badges; absent when badges are unavailable. */
  badges?: Omit<ItemBadges, 'openSignal'>
}

function clock(sec: number): string {
  const s = Math.round(sec)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Full-screen viewer (design: Media viewer): ←/→ and swipe step, Esc closes, Select in the header,
 *  N edits the note and B opens the Badges — stepping through items is the editing mode. */
export function MediaViewer({ items, index, onIndex, onClose, hasMore, onLoadMore, selected = false, onToggle, mobile = false, notes, badges }: Props) {
  const { t, i18n } = useTranslation('library')
  const item = items[index]
  const touchX = useRef<number | null>(null)
  const [editNote, setEditNote] = useState(0)
  const [openBadges, setOpenBadges] = useState(0)
  // Desktop: the media's fitted box, so the description and Badges sit right under
  // it at its width instead of across the whole column.
  const [aspect, setAspect] = useState<number | null>(null)
  const col = useRef<HTMLDivElement | null>(null)
  const annot = useRef<HTMLDivElement | null>(null)
  const [room, setRoom] = useState({ w: 0, h: 0, notes: 0 })
  const annotated = Boolean(notes || badges)
  useLayoutEffect(() => {
    if (mobile) return
    const measure = () =>
      setRoom({ w: col.current?.clientWidth ?? 0, h: col.current?.clientHeight ?? 0, notes: annot.current?.offsetHeight ?? 0 })
    measure()
    const ro = new ResizeObserver(measure)
    if (col.current) ro.observe(col.current)
    if (annot.current) ro.observe(annot.current)
    return () => ro.disconnect()
  }, [mobile, item?.id, annotated])

  const go = async (d: number) => {
    const next = index + d
    if (next < 0) return
    if (next < items.length) return onIndex(next)
    if (hasMore) {
      await onLoadMore()
      onIndex(next)
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Typing a description: arrows move the caret, Esc is the editor's.
      if (e.target instanceof HTMLElement && e.target.closest('textarea, input')) return
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') void go(1)
      else if (e.key === 'ArrowLeft') void go(-1)
      else if (e.metaKey || e.ctrlKey || e.altKey) return
      else if ((e.key === 'n' || e.key === 'N') && notes?.onSave) {
        e.preventDefault()
        setEditNote((n) => n + 1)
      } else if ((e.key === 'b' || e.key === 'B') && badges?.onToggle) {
        e.preventDefault()
        setOpenBadges((n) => n + 1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  if (!item) return null
  const visual = item.type !== 'audio' && aspect
  const fit = (() => {
    if (!visual || !room.w) return null
    const h = Math.max(0, room.h - (room.notes ? room.notes + 12 : 0))
    const w = Math.min(room.w, h * aspect)
    return { w, h: w / aspect }
  })()
  const annotations = (m: boolean) =>
    (notes || badges) && (
      <NotesEditor
        key={item.id}
        value={notes?.value ?? ''}
        onSave={notes?.onSave}
        mobile={m}
        editSignal={editNote}
        badges={badges && <BadgeControl {...badges} openSignal={openBadges} />}
      />
    )
  const date = item.createdAt ? new Date(item.createdAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }) : ''
  const total = hasMore ? `${items.length}+` : String(items.length)
  const swipe = {
    onTouchStart: (e: React.TouchEvent) => (touchX.current = e.touches[0]?.clientX ?? null),
    onTouchEnd: (e: React.TouchEvent) => {
      const dx = (e.changedTouches[0]?.clientX ?? 0) - (touchX.current ?? 0)
      if (Math.abs(dx) > 50) void go(dx < 0 ? 1 : -1)
    },
  }
  const atEnd = index >= items.length - 1 && !hasMore

  if (mobile) {
    return (
      <div className="lib-viewer lib-viewer--m" role="dialog" aria-modal="true" aria-label={t('viewer.label')}>
        <div className="lib-viewer-mtop">
          <button type="button" className="lib-mbtn" aria-label={t('close')} onClick={onClose}>
            <FeatherIcon name="x" size={22} />
          </button>
          <span className="lib-viewer-mpos">{t('viewer.position', { n: index + 1, total })}</span>
          {onToggle ? (
            <button
              type="button"
              role="checkbox"
              aria-checked={selected}
              aria-label={selected ? t('viewer.selected') : t('select')}
              className="lib-mbtn lib-viewer-mcheck"
              onClick={onToggle}
            >
              <span className="lib-check-dot">{selected && '✓'}</span>
            </button>
          ) : (
            <span className="lib-mbtn" aria-hidden="true" />
          )}
        </div>
        <div className="lib-viewer-mstage" {...swipe}>
          <ViewerMedia key={item.id} item={item} />
        </div>
        {annotations(true)}
        <div className="lib-viewer-mfoot">
          <span className="lib-pill">{t(`vault.type.${item.type}`)}</span>
          <span className="lib-viewer-minfo">
            <span className="lib-viewer-date">{date}</span>
            {item.duration > 0 && <span className="lib-viewer-dur">{clock(item.duration)}</span>}
          </span>
          <button type="button" className="lib-viewer-nav" aria-label={t('viewer.previous')} disabled={index === 0} onClick={() => void go(-1)}>
            <FeatherIcon name="chevron-left" size={20} />
          </button>
          <button type="button" className="lib-viewer-nav" aria-label={t('viewer.next')} disabled={atEnd} onClick={() => void go(1)}>
            <FeatherIcon name="chevron-right" size={20} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="lib-viewer" role="dialog" aria-modal="true" aria-label={t('viewer.label')} onClick={onClose}>
      <div className="lib-viewer-head" onClick={(e) => e.stopPropagation()}>
        <span className="lib-pill">{t(`vault.type.${item.type}`)}</span>
        <span className="lib-viewer-date">{date}</span>
        {item.duration > 0 && <span className="lib-viewer-dur">{clock(item.duration)}</span>}
        <span className="lib-spacer" />
        <span className="lib-viewer-pos">{t('viewer.position', { n: index + 1, total })}</span>
        {onToggle && (
          <button type="button" className={`lib-btn ${selected ? 'lib-btn--primary' : ''}`} aria-pressed={selected} onClick={onToggle}>
            <FeatherIcon name={selected ? 'check' : 'plus'} size={14} />
            {selected ? t('viewer.selected') : t('select')}
          </button>
        )}
        <button type="button" className="lib-icon-btn" aria-label={t('close')} onClick={onClose}>
          <FeatherIcon name="x" size={18} />
        </button>
      </div>
      <div className="lib-viewer-body" {...swipe}>
        <button type="button" className="lib-viewer-nav" aria-label={t('viewer.previous')} disabled={index === 0} onClick={(e) => (e.stopPropagation(), void go(-1))}>
          <FeatherIcon name="chevron-left" size={20} />
        </button>
        <div ref={col} className="lib-viewer-col">
          <div className="lib-viewer-stage" style={fit ? { flex: 'none', height: fit.h } : undefined} onClick={(e) => e.stopPropagation()}>
            <ViewerMedia key={item.id} item={item} onAspect={setAspect} />
          </div>
          {annotated && (
            <div ref={annot} className="lib-viewer-annot" style={fit ? { width: Math.min(room.w, Math.max(fit.w, 360)) } : undefined}>
              {annotations(false)}
            </div>
          )}
        </div>
        <button
          type="button"
          className="lib-viewer-nav"
          aria-label={t('viewer.next')}
          disabled={atEnd}
          onClick={(e) => (e.stopPropagation(), void go(1))}
        >
          <FeatherIcon name="chevron-right" size={20} />
        </button>
      </div>
    </div>
  )
}

function ViewerMedia({ item, onAspect }: { item: VaultItem; onAspect?: (ratio: number) => void }) {
  const { t } = useTranslation('library')
  const { loadMedia, streamMedia } = useLibraryDeps()
  const [src, setSrc] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  // Until the first frame (or the image) is in, only the spinner shows: an
  // empty <video> is just floating controls, and its box is not the video's size.
  const [ready, setReady] = useState(false)
  const playable = item.type === 'video' || item.type === 'audio'

  useEffect(() => {
    let live = true
    const url = item.full || item.thumb
    const load = playable && streamMedia ? streamMedia(url) : loadMedia(url).then((s) => (s.kind === 'blocked' ? Promise.reject(new Error('blocked')) : s.src))
    load.then((s) => live && setSrc(s)).catch(() => live && setFailed(true))
    return () => {
      live = false
    }
  }, [item, playable, loadMedia, streamMedia])

  if (failed) return <span className="lib-viewer-ph">{t('vault.noPreview')}</span>
  if (item.type === 'audio' && src) return <audio className="lib-viewer-audio" src={src} controls autoPlay />
  const spinner = !ready && (
    <span className="lib-viewer-loading" role="status" aria-label={t('loading')}>
      <span className="om-spin lib-viewer-spinner" />
    </span>
  )
  if (!src) return spinner
  const cls = `lib-viewer-media${ready ? '' : ' is-loading'}`
  const onError = () => setFailed(true)
  return (
    <>
      {spinner}
      {item.type === 'video' ? (
        <video
          className={cls}
          src={src}
          controls
          autoPlay
          playsInline
          onLoadedData={(e) => {
            const v = e.currentTarget
            if (v.videoWidth && v.videoHeight) onAspect?.(v.videoWidth / v.videoHeight)
            setReady(true)
          }}
          onError={onError}
        />
      ) : (
        <img
          className={cls}
          src={src}
          alt=""
          onLoad={(e) => {
            const i = e.currentTarget
            if (i.naturalWidth && i.naturalHeight) onAspect?.(i.naturalWidth / i.naturalHeight)
            setReady(true)
          }}
          onError={onError}
        />
      )}
    </>
  )
}
